import { useCallback, useEffect, useState, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { SELLER_RMA_LIST } from 'api/graphql/queries/RMA/RMAList';
import { PaginationState } from '@tanstack/react-table';
import { BadgeBg } from 'components/base/Badge';
import { Refund } from 'data/e-commerce/refunds';

// Format date as "MMM d, yyyy, h:mm a" - e.g., "Dec 12, 2025, 12:56 PM"
 const formatRefundDate = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // Get month abbreviation
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    
    // Get day and year
    const day = date.getDate();
    const year = date.getFullYear();
    
    // Get time in 12-hour format with AM/PM
    let hours = date.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${month} ${day}, ${year}, ${hours}:${minutes} ${ampm}`;
  } catch (e) {
    console.error('Error formatting date:', e);
    return dateString; // Return original string if formatting fails
  }
};

// Get status configuration based on status code
const getRefundStatusConfig = (statusCode: number): { code: number; label: string; type: BadgeBg; icon: string } => {
  // Map status codes to human-readable labels and badge types
  switch (statusCode) {
    case 0:
      return { code: 0, label: 'Pending', type: 'warning', icon: 'clock' };
    case 1:
      return { code: 1, label: 'Processing', type: 'info', icon: 'refresh-cw' };
    case 2:
      return { code: 2, label: 'Resolved', type: 'success', icon: 'check-circle' };
    case 3:
      return { code: 3, label: 'Declined', type: 'danger', icon: 'x-circle' };
    case 4:
      return { code: 4, label: 'Canceled', type: 'secondary', icon: 'slash' };
    default:
      return { code: 0, label: 'Unknown', type: 'secondary', icon: 'help-circle' };
  }
};

// Transform API response to match the Refund type
const transformSellerRMA = (sellerRMA: any[]): Refund[] => {
  return sellerRMA.map((item) => {
    
    // Get the status code directly from the API response
    // If the API doesn't provide status codes or if they're all 0, use ID-based simulation for demo purposes
    let statusCode;
    
    if (item.status && typeof item.status.code !== 'undefined') {
      // Convert string status code to number (API returns status as strings)
      statusCode = parseInt(item.status.code, 10);
    } else {
      // For testing/development - create randomized status based on id
      const itemId = parseInt(item.id, 10);
      statusCode = itemId % 5; // This will give values 0-4 distributed across items
    }
    
    // Get full status object
    const statusObj = getRefundStatusConfig(statusCode);
    
    // Transform the API response to match the required Refund format
    return {
      id: item.id || '',
      customer_email: item.customer_email || '',
      customer_name: item.customer_name || 'Unknown Customer',
      created_at: formatRefundDate(item.creation_date) || '', // Map creation_date to created_at
      order_ref: item.order_ref || '',
      order_id: item.order_id || '',
      resolution_type: item.resolution_type || '',
      refunded_amount: typeof item.refunded_amount === 'number' ? item.refunded_amount : 0,
      expedition_number: item.expedition_number || '', // Add expedition_number field
      status: statusObj
    };
  });
};

interface RefundsDataOptions {
  initialPageIndex?: number;
  initialPageSize?: number;
}

interface FilterOptions {
  search?: string; // Combined search for any field
  status?: number | null;
  resolution_type?: number | null;
}

export interface RefundsDataResult {
  tableData: Refund[];
  loading: boolean;
  error: any;
  paginationCount: {
    totalPages: number;
    totalCount: number;
  };
  pagination: PaginationState;
  handlePageChange: (newPageIndex: number) => void;
  handlePageSizeChange: (newPagination: PaginationState) => void;
  fetchRefunds: () => void;
  calculateSumAll: () => number;
  statusCounts: { code: number, count: number }[];
}

export function useRefundsData(
  filterOptions: FilterOptions = {},
  options: RefundsDataOptions = {}
): RefundsDataResult {
  // State for table data
  const [tableData, setTableData] = useState<Refund[]>([]);
  
  // State for pagination
  const [paginationCount, setPaginationCount] = useState({
    totalPages: 0,
    totalCount: 0
  });
  
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: options.initialPageIndex || 0,
    pageSize: options.initialPageSize || 10,
  });

  // Status counts
  const [statusCounts, setStatusCounts] = useState<{ code: number, count: number }[]>([]);
  
  // Function to build active filters for API calls
  const getActiveFilters = useCallback(() => {
    const filters: any = {};
    
    if (filterOptions.search) filters.search = filterOptions.search;
    if (filterOptions.status !== undefined && filterOptions.status !== null) {
      filters.status = filterOptions.status;
    }
    if (filterOptions.resolution_type !== undefined && filterOptions.resolution_type !== null) {
      filters.resolution_type = filterOptions.resolution_type;
    }
    
    return filters;
  }, [filterOptions]);
  
  // Query for fetching refunds
  const [fetchRefundsQuery, { loading, error, data }] = useLazyQuery(SELLER_RMA_LIST, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  
  // Function to fetch refunds with current pagination and filter options
  const fetchRefunds = useCallback(() => {
    fetchRefundsQuery({
      variables: {
        page: pagination.pageIndex + 1,
        page_size: pagination.pageSize,
        filter: getActiveFilters(),
      },
    });
  }, [fetchRefundsQuery, pagination.pageIndex, pagination.pageSize, getActiveFilters]);
  
  // Initial fetch on component mount using a ref to ensure it runs only once
  const initialFetchRef = useRef(false);
  useEffect(() => {
    if (!initialFetchRef.current) {
      fetchRefunds();
      initialFetchRef.current = true;
    }
  }, [fetchRefunds]);
  
  // Pagination handlers
  const handlePageChange = (newPageIndex: number) => {
    setPagination((prev: PaginationState) => {
      const maxPageIndex = Math.max(0, paginationCount.totalPages - 1);
      return {
        ...prev,
        pageIndex: Math.min(newPageIndex, maxPageIndex),
      };
    });
    
    fetchRefundsQuery({
      variables: {
        page: Math.min(newPageIndex + 1, paginationCount.totalPages),
        page_size: pagination.pageSize,
        filter: getActiveFilters(),
      },
    });
  };
  
  // Handle page size changes
  const handlePageSizeChange = (newPagination: PaginationState) => {
    setPagination(newPagination);
    
    // Reset to first page when changing page size
    fetchRefundsQuery({
      variables: {
        page: 1,
        page_size: newPagination.pageSize,
        filter: getActiveFilters(),
      },
    });
  };
  
  // Update the table data when API response is received
  useEffect(() => {
    if (data?.sellerRMAList?.items && data.sellerRMAList.items.length > 0) {
      // Transform the data using the actual status codes from the API
      const transformed = transformSellerRMA(data.sellerRMAList.items);
      setTableData(transformed);
      
      // Update pagination counts
      setPaginationCount({
        totalCount: data.sellerRMAList.total_count || 0,
        totalPages: Math.max(1, data.sellerRMAList.page_info.total_pages || 1),
      });
      
      // Update status counts from the response
      if (data.sellerRMAList.rma_status_count) {
        // Convert status codes in counts from strings to numbers
        const processedCounts = data.sellerRMAList.rma_status_count.map((countItem: any) => {
          return {
            code: typeof countItem.code === 'string' ? parseInt(countItem.code, 10) : countItem.code,
            count: countItem.count
          };
        });
        setStatusCounts(processedCounts);
      } else {
        // For demo/testing: Generate synthetic status counts if not provided by API
        const syntheticCounts = [
          { code: 0, count: Math.floor(Math.random() * 10) + 1 },
          { code: 1, count: Math.floor(Math.random() * 10) + 1 },
          { code: 2, count: Math.floor(Math.random() * 10) + 1 },
          { code: 3, count: Math.floor(Math.random() * 10) + 1 },
          { code: 4, count: Math.floor(Math.random() * 10) + 1 }
        ];
        setStatusCounts(syntheticCounts);
      }
    } else {
      setTableData([]);
    }
  }, [data]);
  
  // Use refs to track previous filter values to avoid unnecessary rerenders
  const prevFilterOptionsRef = useRef('');
  
  // Refetch data when filter options change
  useEffect(() => {
    const currentFilterOptionsString = JSON.stringify(filterOptions);
    
    // Only update if the values have actually changed
    if (currentFilterOptionsString !== prevFilterOptionsRef.current) {
      setPagination((prev) => ({ pageIndex: 0, pageSize: prev.pageSize }));
      
      fetchRefundsQuery({
        variables: {
          page: 1,
          page_size: pagination.pageSize,
          filter: getActiveFilters(),
        },
      });
      
      // Update refs with current values
      prevFilterOptionsRef.current = currentFilterOptionsString;
    }
  }, [filterOptions, pagination.pageSize, fetchRefundsQuery, getActiveFilters]);
  
  // Calculate the total count of all statuses
  const calculateSumAll = useCallback(() => {
    let sum = 0;
    if (statusCounts && statusCounts.length > 0) {
      statusCounts.forEach((item) => {
        sum += item.count;
      });
    }
    return sum;
  }, [statusCounts]);

  return {
    tableData,
    loading,
    error,
    paginationCount,
    pagination,
    handlePageChange,
    handlePageSizeChange,
    fetchRefunds,
    calculateSumAll,
    statusCounts,
  };
}
